import React from "react"
import { FormContainer, Worksheet, LabeledInput, CheckboxYesNo } from "uikit"
import { Dropdown } from "semantic-ui-react"
import { Footer } from "./Footer"
import { useStore } from "store"

const EmergencyFundForm = ({
  data: {
    fields: { amountChoice, amount, use_retirement_accounts },
    isModified,
    onSubmit,
    isPending,
    hasError,
    error
  },
  submitLabel = "Done",
  submitHeader = "",
  onDelete,
  baseUrl,
  ownerOptions,
  amountChoices,
  canAddOwner,
  household,
  isNew
}) => {
  return (
    <Worksheet error={error}>
      <Worksheet.Body>
        <Worksheet.Row
          label="How many months of after-tax income would you like to set aside for an emergency?"
          paddedField
        >
          <Worksheet.Field error={amountChoice.error}>
            <Dropdown
              {...amountChoice}
              data-testid="amountChoice"
              onChange={(event, data) => {
                if (data.value !== "Other") {
                  amount.onChange(event, data)
                }
                amountChoice.onChange(event, data)
              }}
              selection
              fluid
              options={amountChoices}
            />
          </Worksheet.Field>
          {amountChoice.value === "Other" || !!amount.error ? (
            <Worksheet.Row label="Enter the specific amount" paddedField>
              <Worksheet.Field error={amount.error}>
                <LabeledInput
                  {...amount}
                  label=""
                  iconText="$"
                  iconPosition="left"
                  placeholder="0"
                />
              </Worksheet.Field>
            </Worksheet.Row>
          ) : null}
        </Worksheet.Row>
        <Worksheet.Row
          label="Use retirement accounts?"
          paddedField
          description="Check this box if you'd like to consider holding some or all of your emergency fund in your retirement accounts. We take into account the restrictions and tax consequences of doing this. It can be much more tax efficient in the long run, especially if you don't end up using the emergency money."
        >
          <Worksheet.Field>
            <CheckboxYesNo fluid {...use_retirement_accounts} />
          </Worksheet.Field>
        </Worksheet.Row>
      </Worksheet.Body>
      <Footer
        baseUrl={baseUrl}
        isNew={isNew}
        submitLabel={submitLabel}
        onSubmit={onSubmit}
        isPending={isPending}
        onDelete={onDelete}
        isModified={isModified}
        deleteLabel="Delete Goal"
        error={error}
      />
    </Worksheet>
  )
}

const EmergencyFundFormContainer = FormContainer(
  {
    amountChoice: {
      type: "string",
      required: false,
      submit: false
    },
    amount: {
      type: "unit",
      minValue: 1000
    },
    use_retirement_accounts: {
      type: "boolean",
      defaultValue: "false"
    },
    type: {
      type: "string",
      defaultValue: "emergencyfund"
    }
  },
  (fields, ...form) => {}
)(EmergencyFundForm)

const EmergencyFund = ({ household, scenario, goal, ...formProps }) => {
  const [{ net_income }, { addGoal, deleteGoal, updateGoal }] = useStore(
    "incomeBreakdowns",
    household.id
  )
  const isNew = goal === null
  let values = {}

  const amountChoices = [1, 2, 3, 6]
    .map(multiplier => {
      const amount =
        1000 *
        Math.round(Math.max(net_income, 12000) / (12 / multiplier) / 1000)
      const value = amount.toLocaleString()
      return {
        key: amount,
        text: `${multiplier} Month${multiplier > 1 ? "s" : ""} ($${value})`,
        value: value
      }
    })
    .concat({
      key: "Other",
      text: "Other",
      value: "Other"
    })

  if (!isNew) {
    // const goal = await household.getGoal(goalId)
    values = { ...goal }
    const amountChosen = amountChoices.find(({ key }) => key === values.amount)
    if (amountChosen) {
      values.amountChoice = amountChosen.value
    } else {
      values.amountChoice = "Other"
    }
  }

  const submit = isNew
    ? async values =>
        await addGoal({
          scenario_id: scenario.id,
          household_id: household.id,
          ...values
        })
    : async values => await updateGoal({ id: goal.id, ...values })

  const onDelete = async () => {
    await deleteGoal({ scenario_id: scenario.id, id: goal.id })
    return true
  }

  return (
    <EmergencyFundFormContainer
      {...{
        submit,
        onDelete,
        values,
        isNew,
        amountChoices,
        ...formProps
      }}
    />
  )
}

export default EmergencyFund
